import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { embeddedCheckoutEnabled } from '../utils/stripe';
import EmbeddedCheckoutModal from './EmbeddedCheckoutModal';
import { DNET_DEX_URL, formatDnetAmount, TIER_THRESHOLDS, SUBSCRIPTION_PRICES, hasWatcherAccess, hasDeepDiverAccess } from '../utils/tiers';
import { apiFetch, getApiHost } from '../utils/api';
import { trackEvent, trackError } from '../utils/analytics';
import UpgradePicker from './UpgradePicker';
import Header from './Header';
import Footer from './Footer';
import NetworkBackground from './NetworkBackground';
import { peekPendingPhantomTxOperation } from '../utils/phantomDeeplink';

type Cell = boolean | 'credit' | string;
interface Feature { name: string; free: Cell; watcher: Cell; deepDiver: Cell; highlight?: boolean; note?: string }

const features: Feature[] = [
  { name: 'Token details (safety, social, shillers)', free: true, watcher: true, deepDiver: true },
  { name: 'Performers list', free: true, watcher: true, deepDiver: true },
  { name: 'Historical research', free: true, watcher: true, deepDiver: true },
  { name: '10 free signup credits', free: true, watcher: true, deepDiver: true },
  { name: 'Full holder details', free: 'credit', watcher: true, deepDiver: true, highlight: true },
  { name: 'Holder graph', free: 'credit', watcher: true, deepDiver: true, highlight: true },
  { name: 'Trending feed', free: false, watcher: true, deepDiver: true, highlight: true },
  { name: 'Trade fee discount', free: false, watcher: '33% off', deepDiver: '66% off', highlight: true, note: 'Standard platform fee is 0.75% on in-app swap volume.' },
  { name: 'Full API access', free: false, watcher: false, deepDiver: true, highlight: true },
];

const renderCell = (v: Cell) => {
  if (v === true) return <span className="text-green-400">✓</span>;
  if (v === 'credit') return <span className="text-crypto-accent text-xs font-medium" title="Burn 1 credit per token to unlock">1 credit</span>;
  if (typeof v === 'string') return <span className="text-white text-sm font-medium">{v}</span>;
  return <span className="text-gray-600">—</span>;
};

const TiersPage: React.FC = () => {
  const { user, isAuthenticated, refreshUser, openLogin } = useAuth();
  const [subscribing, setSubscribing] = useState<'watcher' | 'deep_diver' | null>(null);
  const [checkoutTier, setCheckoutTier] = useState<'watcher' | 'deep_diver' | null>(null);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [downgrading, setDowngrading] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [pickerTier, setPickerTier] = useState<'watcher' | 'deep_diver' | null>(null);
  // When Phantom redirects back from a signTransaction round-trip
  // initiated by CryptoSubscriptionPay, the URL has
  // `?phantom-tx-pending=1&tier=<tier>` and the operation in
  // localStorage is 'buy-subscription'. Re-open the upgrade modal at
  // the crypto pay screen for that tier so its on-mount continuation
  // can pick up the signed tx.
  const [restoredCryptoPayTier, setRestoredCryptoPayTier] = useState<'watcher' | 'deep_diver' | null>(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('phantom-tx-pending')) return;
    if (peekPendingPhantomTxOperation() !== 'buy-subscription') return;
    const tierParam = params.get('tier');
    if (tierParam !== 'watcher' && tierParam !== 'deep_diver') return;
    setPickerTier(tierParam);
    setRestoredCryptoPayTier(tierParam);
  }, []);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription?\n\nYou\'ll keep access until the end of your current billing period; after that you revert to whatever DNET-only tier you qualify for.')) return;
    setCanceling(true);
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${getApiHost()}/api/stripe/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to cancel subscription');
      }
      await refreshUser();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const handleResume = async () => {
    setResuming(true);
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${getApiHost()}/api/stripe/resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to resume subscription');
      }
      await refreshUser();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to resume subscription');
    } finally {
      setResuming(false);
    }
  };

  const openUpgradePicker = (tier: 'watcher' | 'deep_diver') => {
    // Engagement signal — which tier card got clicked, before we
    // even know if the user is authenticated. Useful for funnel
    // analysis vs `upgrade_picker_shown` (which only fires after
    // the auth gate passes).
    trackEvent('tier_card_clicked', { tier, source: 'tiers_page', isAuthenticated });
    if (!isAuthenticated) {
      openLogin('tiers_page', undefined, { tier });
      return;
    }
    setPickerTier(tier);
  };

  // Refresh user data on mount to get latest DNET balance and tier
  useEffect(() => {
    if (isAuthenticated) refreshUser();
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Funnel entry point.
  useEffect(() => { trackEvent('tiers_page_viewed'); }, []);

  const currentTier = user?.tier || 'free';
  const hasActiveSubscription = user?.subscription?.status === 'active';
  const subscriptionTier = user?.subscription?.tier;

  // Cancellation context — when a subscription is on its way out, we
  // surface "Canceling" on the current card and "Starts <date>" on the
  // tier the user will fall back to (their dnetTier, since DNET is the
  // only signal that survives cancellation). If those are the same,
  // both labels are suppressed since nothing actually changes.
  const sub = user?.subscription;
  const isCanceling = !!sub && (sub.cancelAtPeriodEnd === true || !!sub.cancelAt);
  const cancelDateRaw = sub?.cancelAt || sub?.currentPeriodEnd || null;
  const cancelDateLabel = cancelDateRaw ? new Date(cancelDateRaw).toLocaleDateString() : null;
  const landingTier = isCanceling ? (user?.dnetTier || 'free') : null;
  const willChangeTier = !!landingTier && landingTier !== currentTier;

  const handleSubscribe = async (tier: 'watcher' | 'deep_diver') => {
    if (!isAuthenticated) {
      openLogin('tiers_page', undefined, { tier });
      return;
    }
    trackEvent('subscribe_clicked', { tier, source: 'tiers_page' });
    // Preferred: in-site embedded checkout (no redirect to the Stripe-hosted page).
    if (embeddedCheckoutEnabled) { setCheckoutTier(tier); return; }
    setSubscribing(tier);
    let status = 0;
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${getApiHost()}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ tier }),
      });
      status = response.status;
      const data = await response.json().catch(() => ({} as any));
      if (!response.ok || !data.url) throw new Error(data.error || `Failed to create checkout session (HTTP ${response.status})`);
      window.location.href = data.url;
    } catch (error) {
      trackError(error, { flow: 'stripe_subscription_checkout', tier, status });
      console.error('Subscription error:', error);
      alert(error instanceof Error ? error.message : 'Failed to start subscription');
    } finally {
      setSubscribing(null);
    }
  };

  // In-site embedded subscription checkout: fetch the Checkout Session client_secret.
  const fetchSubClientSecret = useCallback(async (): Promise<string> => {
    if (!checkoutTier) throw new Error('No tier selected');
    const token = localStorage.getItem('deepnets_token');
    const res = await apiFetch(`${getApiHost()}/api/stripe/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ tier: checkoutTier, embedded: true }),
    });
    const data = await res.json().catch(() => ({} as any));
    if (!res.ok || !data.clientSecret) {
      const e = new Error(data.error || `Failed to start checkout (HTTP ${res.status})`);
      trackError(e, { flow: 'stripe_subscription_checkout_embedded', tier: checkoutTier, status: res.status });
      throw e;
    }
    return data.clientSecret;
  }, [checkoutTier]);

  const handleManageSubscription = async () => {
    if (!isAuthenticated) return;
    setManagingSubscription(true);
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${getApiHost()}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create portal session');
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Portal error:', error);
      alert(error instanceof Error ? error.message : 'Failed to open subscription portal');
    } finally {
      setManagingSubscription(false);
    }
  };

  const handleUpgrade = async () => {
    if (!isAuthenticated) return;
    if (!window.confirm('Upgrade to Deep Diver ($150/mo)?\n\nYou\'ll be charged the prorated difference for the rest of this billing cycle. Your card on file will be charged immediately.')) return;
    setUpgrading(true);
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${getApiHost()}/api/stripe/upgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ tier: 'deep_diver' }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to upgrade');
      }
      await refreshUser();
      alert('Upgraded to Deep Diver! 🔱');
    } catch (error) {
      console.error('Upgrade error:', error);
      alert(error instanceof Error ? error.message : 'Failed to upgrade');
    } finally {
      setUpgrading(false);
    }
  };

  const handleDowngrade = async () => {
    if (!isAuthenticated) return;
    if (!window.confirm('Downgrade to Watcher ($10/mo)?\n\nYou\'ll keep Deep Diver access until the end of your current billing period, then switch to Watcher.')) return;
    setDowngrading(true);
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${getApiHost()}/api/stripe/downgrade`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ tier: 'watcher' }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to downgrade');
      }
      const data = await response.json();
      alert(data.message);
      await refreshUser();
    } catch (error) {
      console.error('Downgrade error:', error);
      alert(error instanceof Error ? error.message : 'Failed to downgrade');
    } finally {
      setDowngrading(false);
    }
  };

  return (
    <div className="min-h-screen bg-crypto-dark text-white relative">
      <NetworkBackground />
      <Header />
      <main className="max-w-5xl mx-auto px-6 py-12 relative z-10">
        {/* Hero */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">
            Access Tiers
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Unlock early alpha by holding $DNET or subscribing. Be the first to access new research on Deepnets watchlist tokens.
          </p>
          <p className="text-gray-500 text-sm mt-3">
            Hold {formatDnetAmount(TIER_THRESHOLDS.watcher)} $DNET for Watcher or {formatDnetAmount(TIER_THRESHOLDS.deep_diver)} for Deep Diver — or subscribe from ${SUBSCRIPTION_PRICES.watcher}/mo.
          </p>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {/* Free */}
          <div className={`relative rounded-xl border p-6 ${currentTier === 'free' ? 'border-gray-500 bg-gray-800/30' : 'border-gray-700 bg-crypto-gray'}`}>
            {currentTier === 'free' && !willChangeTier && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="text-xs font-semibold bg-gray-600 text-gray-200 px-3 py-1 rounded-full">Current</span>
              </div>
            )}
            {willChangeTier && landingTier === 'free' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="text-xs font-semibold bg-amber-500 text-amber-950 px-3 py-1 rounded-full whitespace-nowrap">Starts {cancelDateLabel}</span>
              </div>
            )}
            <div className="text-center mb-6">
              <span className="text-3xl">🌐</span>
              <h3 className="text-xl font-bold mt-2">Free</h3>
              <p className="text-gray-500 text-sm mt-1">No hold required</p>
              <p className="text-2xl font-bold text-white mt-3">$0</p>
            </div>
            {/* Low-priority cancel link — only when the user has an
                active, non-canceling subscription. Lets them downgrade
                back to Free without needing the Stripe portal. */}
            {sub && sub.status === 'active' && !isCanceling && (
              <div className="text-center -mt-2">
                <button
                  onClick={handleCancelSubscription}
                  disabled={canceling}
                  className="text-xs text-gray-500 hover:text-gray-300 underline-offset-2 hover:underline transition-colors disabled:opacity-50"
                >
                  {canceling ? 'Canceling…' : 'Cancel subscription'}
                </button>
              </div>
            )}
          </div>

          {/* Watcher */}
          <div className={`rounded-xl border p-6 ${currentTier === 'watcher' ? 'border-crypto-accent bg-crypto-accent/5' : 'border-gray-700 bg-crypto-gray'} relative`}>
            {currentTier === 'watcher' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${willChangeTier ? 'bg-amber-500 text-amber-950' : 'bg-crypto-accent text-crypto-dark'}`}>
                  {willChangeTier ? `Canceling · ends ${cancelDateLabel}` : 'Current'}
                </span>
              </div>
            )}
            {willChangeTier && landingTier === 'watcher' && currentTier !== 'watcher' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="text-xs font-semibold bg-amber-500 text-amber-950 px-3 py-1 rounded-full whitespace-nowrap">Starts {cancelDateLabel}</span>
              </div>
            )}
            <div className="text-center mb-6">
              <span className="text-3xl">👁️</span>
              <h3 className="text-xl font-bold mt-2 text-crypto-accent">Watcher</h3>
              <p className="text-gray-400 text-sm mt-3">Hold {formatDnetAmount(TIER_THRESHOLDS.watcher)} $DNET</p>
              <div className="flex items-center justify-center gap-3 my-3">
                <span className="text-gray-600">—</span>
                <span className="text-gray-500 text-xs uppercase tracking-wider">or</span>
                <span className="text-gray-600">—</span>
              </div>
              <p className="text-2xl font-bold text-white">${SUBSCRIPTION_PRICES.watcher}<span className="text-sm font-normal text-gray-400">/mo</span></p>
            </div>
            {currentTier === 'watcher' && isCanceling && (
              <button
                onClick={handleResume}
                disabled={resuming}
                className="w-full bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                {resuming ? 'Resuming…' : 'Renew Subscription'}
              </button>
            )}
            {hasActiveSubscription && subscriptionTier === 'deep_diver' && (
              <div className="text-center mb-4">
                <button
                  onClick={handleDowngrade}
                  disabled={downgrading}
                  className={`text-xs font-medium transition-colors disabled:opacity-50 ${isCanceling ? 'w-full bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-semibold px-4 py-2.5 rounded-lg' : 'text-gray-400 hover:text-gray-300'}`}
                >
                  {downgrading ? 'Processing…' : isCanceling ? 'Upgrade to Watcher' : 'Downgrade to Watcher →'}
                </button>
              </div>
            )}
            {!hasWatcherAccess(currentTier) && (
              <div className="text-center space-y-2">
                {hasActiveSubscription ? (
                  <button
                    onClick={handleManageSubscription}
                    disabled={managingSubscription}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50"
                  >
                    {managingSubscription ? 'Loading…' : 'Manage Subscription'}
                  </button>
                ) : (
                  <button
                    onClick={() => openUpgradePicker('watcher')}
                    className="w-full bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm"
                  >
                    Upgrade to Watcher
                  </button>
                )}
                <p className="text-gray-500 text-xs">
                  Hold $DNET · Pay monthly with crypto · Card subscription
                </p>
              </div>
            )}
          </div>

          {/* Deep Diver */}
          <div className={`relative rounded-xl border p-6 ${currentTier === 'deep_diver' ? 'border-purple-500 bg-purple-500/5' : 'border-gray-700 bg-crypto-gray'}`}>
            {currentTier === 'deep_diver' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className={`text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap ${willChangeTier ? 'bg-amber-500 text-amber-950' : 'bg-purple-500 text-white'}`}>
                  {willChangeTier ? `Canceling · ends ${cancelDateLabel}` : 'Current'}
                </span>
              </div>
            )}
            <div className="text-center mb-6">
              <span className="text-3xl">🔱</span>
              <h3 className="text-xl font-bold mt-2 text-purple-400">Deep Diver</h3>
              <p className="text-gray-400 text-sm mt-3">Hold {formatDnetAmount(TIER_THRESHOLDS.deep_diver)} $DNET</p>
              <div className="flex items-center justify-center gap-3 my-3">
                <span className="text-gray-600">—</span>
                <span className="text-gray-500 text-xs uppercase tracking-wider">or</span>
                <span className="text-gray-600">—</span>
              </div>
              <p className="text-2xl font-bold text-white">${SUBSCRIPTION_PRICES.deep_diver}<span className="text-sm font-normal text-gray-400">/mo</span></p>
            </div>
            {currentTier === 'deep_diver' && isCanceling && (
              <button
                onClick={handleResume}
                disabled={resuming}
                className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50"
              >
                {resuming ? 'Resuming…' : 'Renew Subscription'}
              </button>
            )}
            {!hasDeepDiverAccess(currentTier) && (
              <div className="text-center space-y-2">
                {hasActiveSubscription && subscriptionTier === 'watcher' ? (
                  <button
                    onClick={handleUpgrade}
                    disabled={upgrading}
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50"
                  >
                    {upgrading ? 'Upgrading…' : 'Upgrade to Deep Diver'}
                  </button>
                ) : hasActiveSubscription ? (
                  <button
                    onClick={handleManageSubscription}
                    disabled={managingSubscription}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm disabled:opacity-50"
                  >
                    {managingSubscription ? 'Loading…' : 'Manage Subscription'}
                  </button>
                ) : (
                  <button
                    onClick={() => openUpgradePicker('deep_diver')}
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm"
                  >
                    Upgrade to Deep Diver
                  </button>
                )}
                <p className="text-gray-500 text-xs">
                  Hold $DNET · Pay monthly with crypto · Card subscription
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Feature comparison table */}
        <div className="rounded-xl border border-gray-700 overflow-hidden mb-12">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-700 bg-crypto-gray">
                <th className="text-left px-6 py-4 text-gray-400 font-medium text-sm">Feature</th>
                <th className="text-center px-4 py-4 text-gray-400 font-medium text-sm">Free</th>
                <th className="text-center px-4 py-4 text-crypto-accent font-medium text-sm">👁️ Watcher</th>
                <th className="text-center px-4 py-4 text-purple-400 font-medium text-sm">🔱 Deep Diver</th>
              </tr>
            </thead>
            <tbody>
              {features.map((f, i) => (
                <tr key={i} className={`border-b border-gray-800 ${f.highlight ? 'bg-purple-900/10' : ''}`}>
                  <td className="px-6 py-3 text-sm text-gray-300">
                    {f.highlight && <span className="text-purple-400 mr-1">★</span>}
                    {f.name}
                    {f.note && <div className="text-gray-500 text-xs mt-0.5">{f.note}</div>}
                  </td>
                  <td className="text-center px-4 py-3">{renderCell(f.free)}</td>
                  <td className="text-center px-4 py-3">{renderCell(f.watcher)}</td>
                  <td className="text-center px-4 py-3">{renderCell(f.deepDiver)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-gray-500 text-xs text-center -mt-8 mb-12">
          <span className="text-crypto-accent">1 credit</span> = burn one credit to unlock that feature for a single token (one-time, permanent for that token).
        </p>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-center">FAQ</h2>
          <div className="space-y-4">
            <div className="bg-crypto-gray rounded-xl border border-gray-700 p-5">
              <h4 className="font-semibold mb-2">What are my options for unlocking access?</h4>
              <p className="text-gray-400 text-sm">
                You have two ways to unlock Watcher or Deep Diver access: <strong className="text-white">hold $DNET</strong> in your connected wallet for permanent access as long as you hold, or <strong className="text-white">subscribe monthly</strong> for a fixed fee. Either works — your choice.
              </p>
            </div>
            <div className="bg-crypto-gray rounded-xl border border-gray-700 p-5">
              <h4 className="font-semibold mb-2">Can I have both $DNET holdings and a subscription?</h4>
              <p className="text-gray-400 text-sm">
                Yes. If you have both, we take whichever gives you the higher tier. For example, if you hold enough $DNET for Watcher but also have a Deep Diver subscription, you'll have Deep Diver access.
              </p>
            </div>
            <div className="bg-crypto-gray rounded-xl border border-gray-700 p-5">
              <h4 className="font-semibold mb-2">Can I still use credits without holding or subscribing?</h4>
              <p className="text-gray-400 text-sm">
                Yes. Free users can burn 1 credit per token to unlock <strong className="text-white">full holder details</strong> or the <strong className="text-white">holder graph</strong> for that specific token — useful for the occasional deep look without committing to a tier. Watcher and Deep Diver get unlimited access to those without spending credits.
              </p>
            </div>
            <div className="bg-crypto-gray rounded-xl border border-gray-700 p-5">
              <h4 className="font-semibold mb-2">What if I sell my $DNET?</h4>
              <p className="text-gray-400 text-sm">
                Your tier is checked in real-time based on wallet balance. If your balance drops below the threshold, you'll revert to the appropriate tier (or your subscription tier, if active). No penalty — just rebuy when you want access back.
              </p>
            </div>
            <div className="bg-crypto-gray rounded-xl border border-gray-700 p-5">
              <h4 className="font-semibold mb-2">Where do I buy $DNET?</h4>
              <p className="text-gray-400 text-sm">
                $DNET is available on Jupiter and other Solana DEXes. Fixed 1B supply, 100% burn mechanics.{' '}
                <a
                  href={DNET_DEX_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('buy_dnet_clicked', { source: 'tiers_page_faq' })}
                  className="text-crypto-accent hover:underline"
                >
                  Buy on Jupiter →
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        {!hasWatcherAccess(currentTier) && (
          <div className="text-center mt-12">
            <a
              href={DNET_DEX_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('buy_dnet_clicked', { source: 'tiers_page_cta' })}
              className="inline-flex items-center gap-2 bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-bold px-8 py-3 rounded-lg text-lg transition-colors"
            >
              Buy $DNET and Start Watching →
            </a>
          </div>
        )}
      </main>
      <Footer />
      <UpgradePicker
        target={pickerTier}
        onClose={() => { setPickerTier(null); setRestoredCryptoPayTier(null); }}
        onSubscribe={handleSubscribe}
        subscribing={subscribing}
        defaultShowCryptoPay={restoredCryptoPayTier === pickerTier && pickerTier !== null}
      />
      {checkoutTier && (
        <EmbeddedCheckoutModal
          title={`Subscribe · ${checkoutTier === 'deep_diver' ? 'Deep Diver' : 'Watcher'}`}
          fetchClientSecret={fetchSubClientSecret}
          onClose={() => setCheckoutTier(null)}
        />
      )}
    </div>
  );
};

export default TiersPage;
